import { useMemo, useCallback, useState, useEffect } from 'react';
import { produce } from 'immer';
import { css, type Theme, useTheme } from '@emotion/react';
import { UserCircle, Plus, X } from '@phosphor-icons/react';
import { useUser } from 'client/lib/User';
import { DeploymentClientState, useDeploymentClientState } from 'client/state/DeploymentClientState';
import { useGitHubState } from 'client/state/GitHubState';
import { Banner } from 'client/components/Banner';
import { Button } from 'client/components/Button';
import { Catalog, CatalogItemProps } from 'client/components/Catalog';
import { EmptyGitHubNotice } from 'client/components/GitHub/EmptyGitHubNotice';
import { Stack } from 'client/components/Stack';
import { Tag } from 'client/components/Tag';

export const SettingsGit = ({ isCreate }: { isCreate?: boolean }) => {
  const theme = useTheme();
  const { user } = useUser();
  const githubUser = useGitHubState((state) => state.user);
  const isAuthenticated = useGitHubState((state) => state.isAuthenticated);
  const gitConfigUser = useDeploymentClientState((state) => state.document?.gitConfigUser);
  const isQuickLaunch = useDeploymentClientState((state) => state.document?.repositoryUrl === null);
  const isSelfAssigned = githubUser?.githubUsername === gitConfigUser?.username;
  const [touched, setTouched] = useState(false);

  const handleAuthenticate = () => useGitHubState.setState({ isDialogOpen: true });

  const handleRemove = useCallback(
    () =>
      useDeploymentClientState.setState(
        produce((state: DeploymentClientState) => {
          state.document!.gitConfigUser = null;
        })
      ),
    []
  );

  const handleAssign = useCallback(() => {
    if (!user) return;
    if (!githubUser) return;

    setTouched(true);

    useDeploymentClientState.setState(
      produce((state: DeploymentClientState) => {
        state.document!.gitConfigUser = {
          userID: user.userID,
          firstName: user.firstName,
          lastName: user.lastName,
          username: githubUser.githubUsername,
          image: user.avatar || '',
        };
      })
    );
  }, [user, githubUser]);

  // When everything loads for the first time, pre-set assigned
  useEffect(() => {
    if (!isCreate) return;
    if (touched) return;
    handleAssign();
  }, [touched, handleAssign]);

  const emptyItem = useMemo(
    () => ({
      id: 'empty',
      title: 'Empty',
      subtitle: 'No automatic Git access will be configured',
      icon: <UserCircle color={theme.color.grey[80]} />,
      ornamentIcon: <X color={theme.color.grey[80]} />,
      action: (
        <Button icon={<Plus weight="bold" />} disabled={isSelfAssigned || isQuickLaunch} onClick={handleAssign}>
          Assign Self
        </Button>
      ),
    }),
    [isQuickLaunch, isSelfAssigned, handleAssign]
  );

  const catalogItem = useMemo(() => {
    if (!gitConfigUser) return null;

    const name = `${gitConfigUser.firstName} ${gitConfigUser.lastName}`.trim() || gitConfigUser.username;

    return {
      id: `${gitConfigUser.userID}`,
      title: (
        <>
          {githubUser?.githubUsername === gitConfigUser.username && <Tag>You</Tag>} {name}
        </>
      ),
      subtitle: `@${gitConfigUser.username}`,
      image: {
        alt: `profile picture`,
        src: `https://github.com/${gitConfigUser.username}.png?size=32`,
        shape: 'circle',
      },
      action: (
        <Button icon={<Plus weight="bold" />} disabled={isSelfAssigned || isQuickLaunch} onClick={handleAssign}>
          Assign Self
        </Button>
      ),
    } as CatalogItemProps;
  }, [gitConfigUser, githubUser?.githubUsername, isQuickLaunch, isSelfAssigned, handleRemove]);

  if ((!isAuthenticated || !user) && !isQuickLaunch) return <EmptyGitHubNotice variant="error" onAction={handleAuthenticate} />;

  return (
    <Stack>
      <Catalog items={[catalogItem || emptyItem]} css={catalogStyle} />

      <Banner>Commits created in this deployment will be credited to the selected user.</Banner>

      {!gitConfigUser && !isQuickLaunch && (
        <Banner type="warning">
          With no automated Git access, you will have to authenticate manually. There are several ways to accomplish this, but they may
          involve certain security risks. You can read more <a href="#">here</a>.
        </Banner>
      )}
    </Stack>
  );
};

const catalogStyle = css`
  .MuiAvatar-rounded {
    border-radius: 50%;
  }
`;

const removeStyle = (theme: Theme) => css`
  margin-left: 8px;
  color: ${theme.color.red[100]};
`;
